<template>
  <div class="bdc_main_wrap whero">
    <div class="thw_content">
      <div class="internal-sub-nav">
        <div
          v-for="(tab, i) in tabOptions"
          :key="i"
          class="isn-item"
          :class="{ active: tab.id === selectedTab, disabled: tab.disabled }"
          @click="onTabClick(tab)"
        >
          {{ tab.value }}
        </div>
      </div>
    </div>
    <div v-if="tableauJwtToken && selectedTab === 'ad-previews'">
      <tableau-viz id="tableauViz" :src="tableauViewURL" :token="tableauJwtToken" toolbar="top">
        <custom-parameter name=":showShareOptions" value="false"></custom-parameter>
        <viz-filter field="Start Date" :value="startDate"></viz-filter>
        <viz-filter field="End Date" :value="endDate"></viz-filter>
        <!-- <viz-filter field="Advertiser" :value="xandrAdvertiserId"></viz-filter> -->
        <viz-filter field="Advertiser" :value="escapedAdvertiserName"></viz-filter>
        <!-- <viz-filter field="Agency" :value="agency"></viz-filter> -->
        <viz-filter field="Contract ID" :value="campaigns"></viz-filter>
        <viz-filter field="ConLine ID" :value="adReadyIdList"></viz-filter>
        <viz-parameter field="embed" value="yes"></viz-parameter>
      </tableau-viz>
    </div>
    <!-- Component for Campaigns/Ad group table -->
    <div v-else>
      <div id="performanceAnalysis"></div>
      <div
        id="performanceAnalysis_container"
        class="bdc_panel full-width expan def-box"
        :class="isCampaignTableExpanded ? 'expd' : ''"
      >
        <performance-analysis
          :section-title="selectedTabTitle"
          :tab-options="paTabOptions"
          :meta-data="paMetaData"
          :rows="paRows"
          :loading="paLoading || filtersAppliedLoading"
          :is-table-expanded="isCampaignTableExpanded"
          :sort-by="paSortBy"
          :is-brand-table="true"
          :sort-direction="paSortDirection"
          @on-tab-click="handlePATabClick"
          @on-sort-direction-change="handlePaSortDirectionChange"
          @on-change-sort-by="handleSortByChange"
          @on-table-expand="handleTableExpand"
          @on-export="handleExportClick"
        />
      </div>
      <!-- Component for Metric bar chart -->
      <div
        id="metricDistribution_Container"
        class="bdc_panel two-thirds last"
        style="height: 520px;position: relative;"
      >
        <metric-distribution
          section-title="Geo // Metric"
          :tab-options="selectedTab === 'geo' ? [] : metricDistOptions"
          :hide-dimension-options="selectedTab === 'geo' ? false : true"
          :dimension-options="selectedTab === 'geo' ? distTabOptions : []"
          :metric-options="barMetricOptions"
          :rows="barMetricRows"
          :loading="barMetricLoading || filtersAppliedLoading"
          :dimension="selectedTab"
          @on-options-change="handleBarMetricOptionsChange"
          @on-export="handleExportClick"
        />
      </div>
      <!-- Component for Distribution -->
      <div id="distribution_container" class="bdc_panel one-thirds" style="height: 520px;">
        <distribution
          :tab-options="selectedTab === 'geo' ? [] : distTabOptions"
          :hide-dimension-options="selectedTab === 'geo' ? false : true"
          :dimension-options="selectedTab === 'geo' ? distTabOptions : []"
          :metric-options="distMetricOptions"
          :rows="distRows"
          :loading="distLoading || filtersAppliedLoading"
          :dimension="selectedTab"
          :donut-size="220"
          donut-component-id="sChartsLargeChart"
          @on-options-change="handleDistOptionsChange"
          @on-export="handleExportClick"
        />
      </div>
    </div>
  </div>
</template>
<script>
import _ from 'underscore';
import { get } from 'vuex-pathify';
import { uuid } from '@/helpers/global/misc-helpers';
import axios from 'axios';
import { getCorrectLabels, downloadCSVFile } from '@/util/utility-functions';
import {
  ACCOUNT_SPECIFIC_CAMPAIGN_SUB_TAB_OPTIONS,
  ACCOUNT_SPECIFIC_DELIVERY_TAB_OPTIONS,
  CAMPAIGN_SUB_TAB_OPTIONS,
  DELIVERY_TAB_OPTIONS,
  DISTRIBUTION_MEASUREMENT_OPTIONS,
  DELIVERY_METRIC_OPTIONS,
  PERFORMANCE_ANALYSIS_DIMENSION_COLUMN,
  PERFORMANCE_ANALYSIS_COLUMNS_PER_DIMENSION,
  PERFORMANCE_ANALYSIS_DIMENSION_TITLES,
  ACCOUNT_SPECIFIC_PERFORMANCE_ANALYSIS_DIMENSION_TITLES,
} from '@/search-constant';
import { VIA_ACCOUNT_ID } from '@/constant';
import PerformanceAnalysis from '~/components/performance-analysis.vue';
import Distribution from '~/components/distribution.vue';
import MetricDistribution from '~/components/metric-distribution.vue';

import appConsoleDataLayer from '~/api/app-console-data';
import { formatDateForAPI } from '~/util/apiDateFormat';
import config from '~/config';

export default {
  components: {
    PerformanceAnalysis,
    Distribution,
    MetricDistribution,
  },
  props: {
    activeSolutionId: {
      type: Number,
      required: false,
      default: 1,
    },
    isCampaignTableExpanded: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    let initialDimension = 'campaign';
    const tabOptions = DELIVERY_TAB_OPTIONS;

    let selectedTab = '';
    let selectedTabTitle = '';
    let selectedTabUrl = '';
    if (tabOptions?.length) {
      const defaultSelectedTab = tabOptions.find((t) => t.selected) || this.tabOptions[0];
      selectedTab = defaultSelectedTab?.id || tabOptions[0].id;
      selectedTabTitle = defaultSelectedTab?.value || tabOptions[0].value;
      selectedTabUrl = defaultSelectedTab?.viewUrl || tabOptions[0].viewUrl;
      initialDimension = selectedTab;

      if (defaultSelectedTab?.subTabs?.length) {
        const defaultSelectedSubTab = defaultSelectedTab?.subTabs?.find((t) => t.selected);
        initialDimension = defaultSelectedSubTab?.id || defaultSelectedTab?.subTabs[0].id;
      }
    }

    const distMetricOptions = DISTRIBUTION_MEASUREMENT_OPTIONS;
    const selectedDistMetric = distMetricOptions?.length ? distMetricOptions[0].id : '';
    const selectedDistPixelIds = [];
    const barMetricOptions = DELIVERY_METRIC_OPTIONS;
    const selectedBarMetric = barMetricOptions?.length ? barMetricOptions[0].id : '';
    const selectedBarPixelIds = [];
    return {
      tabOptions,
      selectedTab,
      selectedTabTitle,
      selectedTabUrl,
      tableauJwtToken: null,
      selectedDistDimension: initialDimension,
      selectedDistMetric,
      selectedDistPixelIds,
      distRows: [],
      distLoading: false,
      selectedBarDimension: initialDimension,
      selectedBarMetric,
      selectedBarPixelIds,
      barMetricRows: [],
      barMetricLoading: false,
      selectedPADimension: initialDimension,
      paRequestId: '',
      paMetaData: [],
      paRows: [],
      paSortBy: '',
      paSortDirection: 'asc',
      paLoading: false,
    };
  },
  computed: {
    account: get('common/account'),
    advertiser: get('common/advertiser'),
    isViaAccount: get('common/isViaAccount'),
    dates: get('dashboard/dates'),
    selectedCampaigns: get('dashboard/GET_SELECTED_CAMPAIGNS'),
    selectedCampaignIds: get('dashboard/GET_SELECTED_CAMPAIGN_IDS'),
    selectedAdGrpOptStr: get('dashboard/GET_SELECTED_AD_GROUP_STR'),
    selectedMediaTypes: get('dashboard/GET_SELECTED_MEDIATYPES'),
    selectedKeywords: get('dashboard/GET_SELECTED_KEYWORDS'),
    selectedCreatives: get('dashboard/GET_SELECTED_CREATIVES'),
    selectedAudiences: get('dashboard/GET_SELECTED_AUDIENCES'),
    campaignOptions: get('dashboard/filters@campaignOptions'),
    adGroupOptions: get('dashboard/filters@adGroupOptions'),
    pixelOptions: get('dashboard/filters@pixelOptions'),
    selectedPixelIds: get('dashboard/GET_SELECTED_PIXEL_IDS'),
    filtersApplied: get('dashboard/filtersApplied'),
    filtersAppliedLoading: get('dashboard/filtersAppliedLoading'),
    mediaTypeOptions: get('dashboard/filters@mediaTypeOptions'),
    keywordOptions: get('dashboard/filters@keywordOptions'),
    audienceOptions: get('dashboard/filters@audienceOptions'),
    creativeOptions: get('dashboard/filters@creativeOptions'),
    selectedDashboardTab: get('dashboard/selectedDashboardTab'),
    pixelDropdownOptions() {
      if (!this.pixelOptions) {
        return [];
      }

      if (this.pixelOptions.length === 1) {
        return [];
      }

      return this.pixelOptions;
    },
    distMetricOptions() {
      let options = JSON.parse(JSON.stringify(DISTRIBUTION_MEASUREMENT_OPTIONS));
      if (this.account.id === VIA_ACCOUNT_ID) {
        options = options.filter((option) => option.id !== 'spend');
      }
      if (['geo', 'keywords'].includes(this.selectedTab)) {
        return options.filter((dist) => !['conversions', 'revenue'].includes(dist.id));
      }
      // let options = JSON.parse(JSON.stringify(DISTRIBUTION_MEASUREMENT_OPTIONS));

      const conversionOptions = options.filter((o) => ['conversions', 'revenue'].includes(o.id));
      if (conversionOptions && this.pixelDropdownOptions && this.pixelDropdownOptions.length > 0) {
        conversionOptions.forEach((o) => {
          o.children = [];
          this.pixelDropdownOptions.forEach((p) =>
            o.children.push({ value: p.value, id: p.key, selected: true })
          );
        });
      }

      return options;
    },
    barMetricOptions() {
      let barMetric = [];
      if (['geo', 'keywords'].includes(this.selectedTab)) {
        barMetric = DELIVERY_METRIC_OPTIONS.filter((dist) => !['cpa', 'roas'].includes(dist.id));
        if (this.account.id === VIA_ACCOUNT_ID) {
          barMetric = barMetric.filter((op) => op.id !== 'cpc');
        }
        return barMetric;
      }
      let options = JSON.parse(JSON.stringify(DELIVERY_METRIC_OPTIONS));

      const conversionOptions = options.filter((o) => ['cpa', 'roas'].includes(o.id));
      if (conversionOptions && this.pixelDropdownOptions && this.pixelDropdownOptions.length > 0) {
        conversionOptions.forEach((o) => {
          o.children = [];
          this.pixelDropdownOptions.forEach((p) =>
            o.children.push({ value: p.value, id: p.key, selected: true })
          );
        });
      }
      if (this.account.id === VIA_ACCOUNT_ID) {
        options = options.filter((op) => op.id !== 'cpc');
      }
      return options;
    },
    advertiserId() {
      return this.advertiser ? this.advertiser.id : 0;
    },
    advertiserName() {
      return this.advertiser ? this.advertiser.name : '';
    },
    escapedAdvertiserName() {
      return this.advertiserName.replace(/,/g, '\\,');
    },
    xandrAdvertiserId() {
      return this.advertiser ? this.advertiser.xandrAdvertiserId : 0;
      // return '3884073415';
    },
    agencyId() {
      return this.account ? this.account.id : 0;
    },
    agency() {
      return this.account ? this.account.name : '';
    },
    payload() {
      return {
        advertiser: this.advertiser ? this.advertiser.name : '',
        client: this.account ? this.account.name : '',
        campaignIds: this.selectedCampaignIds,
        adGroups: this.selectedAdGrpOptStr,
        startDate: formatDateForAPI(this.dates.startDate),
        endDate: formatDateForAPI(this.dates.endDate),
        mediaTypes: this.selectedMediaTypes,
        keywords: this.selectedKeywords,
        audiences: this.selectedAudiences,
        creatives: this.selectedCreatives,
        pixelIds: this.selectedPixelIds,
      };
    },
    metricDistOptions() {
      if (this.selectedTab === 'campaign_adgroup') {
        return getCorrectLabels(
          CAMPAIGN_SUB_TAB_OPTIONS,
          ACCOUNT_SPECIFIC_CAMPAIGN_SUB_TAB_OPTIONS[this.account.id]
        );
      }
      const currentTab = this.tabOptions.find((t) => t.id === this.selectedTab);
      return currentTab?.subTabs || [];
    },
    distTabOptions() {
      if (this.selectedTab === 'campaign_adgroup') {
        return getCorrectLabels(
          CAMPAIGN_SUB_TAB_OPTIONS,
          ACCOUNT_SPECIFIC_CAMPAIGN_SUB_TAB_OPTIONS[this.account.id]
        );
      }
      const currentTab = this.tabOptions.find((t) => t.id === this.selectedTab);
      return currentTab?.subTabs || [];
    },
    paTabOptions() {
      const currentTab = this.tabOptions.find((t) => t.id === this.selectedTab);
      return currentTab?.subTabs || [];
    },
    tableauViewURL() {
      return `${config.TABLEAU_URL}${this.selectedTabUrl}`;
    },
    startDate() {
      return formatDateForAPI(this.dates.startDate);
    },
    endDate() {
      return formatDateForAPI(this.dates.endDate);
    },
    adReadyIdList() {
      const { adGroupOptions } = this;
      const uncheckedAdGroups = this.adGroupOptions.find((item) => item.checked === false);
      if (!uncheckedAdGroups) {
        return '';
      }
      if (adGroupOptions.length === 0) {
        return '';
      }

      const selectedAdGroupOptions = adGroupOptions.filter((f) => f.checked);
      if (selectedAdGroupOptions.length >= 0) {
        let queryStrArray = [];
        selectedAdGroupOptions.forEach((adGroup) => {
          const { adreadyIds } = adGroup;
          adreadyIds.forEach((lid) => {
            queryStrArray.push(lid.adreadyId);
          });
        });
        queryStrArray = queryStrArray.filter(
          (item, index) => queryStrArray.indexOf(item) === index
        );
        return queryStrArray.join('\\,');
      }
      return '';
    },
    campaignIds() {
      if (this.selectedCampaignIds.length) {
        return this.selectedCampaignIds;
      }
      return '';
    },
    campaigns() {
      const uncheckedCampaigns = this.campaignOptions.find((item) => item.checked === false);
      if (!uncheckedCampaigns) {
        return '';
      }
      if (this.selectedCampaigns.length) {
        return this.selectedCampaigns;
      }
      return '';
    },
  },
  watch: {
    advertiser: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          this.debounceTableauAuthToken();
          await this.loadAllData();
        }
      },
    },

    dates: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (!this.filtersAppliedLoading) {
            this.debounceTableauAuthToken();
            await this.loadAllData();
          }
        }
      },
    },
    filtersApplied: {
      deep: true,
      async handler(n) {
        if (n) {
          this.debounceTableauAuthToken();
          await this.loadAllData();
          this.$store.set('dashboard/filtersApplied', false);
          this.$store.set('dashboard/filtersAppliedLoading', false);
        }
      },
    },
    pixelOptions(newPixelOptions) {
      if (!newPixelOptions) {
        this.selectedDistPixelIds = [];
        this.selectedBarPixelIds = [];
        return;
      }

      if (newPixelOptions.length === 1) {
        this.selectedDistPixelIds = [];
        this.selectedBarPixelIds = [];
        return;
      }

      this.selectedDistPixelIds = newPixelOptions?.map((p) => p.key) || [];
      this.selectedBarPixelIds = newPixelOptions?.map((p) => p.key) || [];
    },
  },
  beforeMount() {
    this.tabOptions = getCorrectLabels(
      DELIVERY_TAB_OPTIONS,
      ACCOUNT_SPECIFIC_DELIVERY_TAB_OPTIONS[this.account.id]
    );
  },
  async mounted() {
    if (!this.isViaAccount) {
      this.tabOptions = this.tabOptions.filter((item) => item.id !== 'ad-previews');
    }

    this.$store.set('dashboard/selectedDashboardTab', 'adgroup');
    const recaptchaScript = document.createElement('script');
    recaptchaScript.setAttribute(
      'src',
      `${config.TABLEAU_URL}/javascripts/api/tableau.embedding.3.latest.js`
    );
    recaptchaScript.setAttribute('type', 'module');
    document.head.appendChild(recaptchaScript);
    this.getTableauAuthToken();
    await this.loadAllData();
  },
  methods: {
    debounceTableauAuthToken() {
      _.debounce(this.getTableauAuthToken(), 100, true);
    },
    async getTableauAuthToken() {
      axios
        .get(`${config.ADREADY_URL}/api/token/tableau`, {
          withCredentials: true,
        })
        .then((d) => {
          this.tableauJwtToken = d.data.result;
        });
    },
    handleExportClick(fileName, csvData) {
      try {
        downloadCSVFile(csvData, fileName);
      } catch (err) {
        console.error(`error downloading delivery ${fileName} file ->`, err);
        throw err;
      }
    },
    async loadAllData() {
      if (!this.advertiser) {
        return;
      }
      const apiFuncs = [
        this.loadPerformanceData(),
        this.loadDistributionData(),
        this.loadMetricDistributionData(),
      ];
      await Promise.all(apiFuncs);
      this.$store.set('dashboard/filtersAppliedLoading', false);
    },
    handleTableExpand(val) {
      this.$emit('on-expand-campaign-table', val);
    },
    async loadDistributionData() {
      this.distLoading = true;
      this.distRows = await appConsoleDataLayer.fetchSearchDistributionData(
        this.advertiserId,
        this.activeSolutionId,
        this.selectedDistDimension,
        this.selectedDistMetric,
        this.selectedDistPixelIds,
        this.payload
      );
      this.distLoading = false;
    },
    async loadMetricDistributionData() {
      this.barMetricLoading = true;
      this.barMetricRows = await appConsoleDataLayer.fetchSearchMetricDistributionData(
        this.advertiserId,
        this.activeSolutionId,
        this.selectedBarDimension,
        this.selectedBarMetric,
        this.selectedBarPixelIds,
        this.payload
      );
      this.barMetricLoading = false;
    },
    async loadPerformanceData() {
      this.paRequestId = uuid();
      this.paLoading = true;
      this.paMetaData = this.fetchPerformanceMetaData(this.selectedPADimension);
      if (['campaign', 'adgroup'].includes(this.selectedPADimension)) {
        this.paSortBy = 'startDate';
        this.paSortDirection = 'desc';
      } else {
        this.paSortBy = '';
        this.paSortDirection = 'asc';
      }
      const response = await appConsoleDataLayer.fetchSearchDimensionPerformance(
        this.paRequestId,
        this.advertiserId,
        this.activeSolutionId,
        this.selectedPADimension,
        this.payload
      );
      if (this.paRequestId === response.requestId) {
        this.paRows = response.data;
        this.paLoading = false;
        return;
      }
      setTimeout(() => {
        this.paLoading = false;
      }, 200);
    },
    fetchPerformanceMetaData(dimension) {
      const list = [PERFORMANCE_ANALYSIS_DIMENSION_COLUMN];
      list[0].value =
        getCorrectLabels(
          PERFORMANCE_ANALYSIS_DIMENSION_TITLES,
          ACCOUNT_SPECIFIC_PERFORMANCE_ANALYSIS_DIMENSION_TITLES[this.account.id]
        )[dimension] || 'Name';
      let columns = PERFORMANCE_ANALYSIS_COLUMNS_PER_DIMENSION[dimension];
      if (!columns) {
        columns = PERFORMANCE_ANALYSIS_COLUMNS_PER_DIMENSION.other;
      }
      if (this.account.id === VIA_ACCOUNT_ID) {
        columns = columns.filter(
          (options) =>
            options.key !== 'spend' &&
            options.key !== 'actualPacing' &&
            options.key !== 'budget' &&
            options.key !== 'cpc'
        );
      }
      list.push(...columns);

      return list;
    },
    onTabClick(tab) {
      if (tab.disabled) {
        return;
      }
      this.selectedTab = tab.id;
      this.selectedTabTitle = tab.value;
      this.selectedTabUrl = tab.viewUrl;

      let selectedDistTab = '';
      let selectedPATab = tab.id;
      this.$store.set('dashboard/selectedDashboardTab', this.selectedTab);
      this.$store.dispatch('dashboard/deSelectAdvanceFilters');
      this.debounceTableauAuthToken();

      if (this.selectedTab === 'ad-previews') return;
      const { distTabOptions, paTabOptions, metricDistOptions } = this;
      if (distTabOptions?.length) {
        const defaultDistTab = distTabOptions?.find((t) => t.selected) || distTabOptions[0];
        selectedDistTab = defaultDistTab?.id || '';
      }
      if (metricDistOptions?.length) {
        const defaultMetricOpt = metricDistOptions?.find((t) => t.selected) || metricDistOptions[0];
        selectedDistTab = defaultMetricOpt?.id || '';
      }
      if (paTabOptions?.length) {
        const defaultPATab = paTabOptions?.find((t) => t.selected) || paTabOptions[0];
        selectedPATab = defaultPATab?.id || tab.id;
      }

      this.handlePATabClick(selectedPATab);
      this.handleDistOptionsChange(selectedDistTab, '', this.selectedDistMetric);
      this.handleBarMetricOptionsChange(selectedDistTab, '', this.selectedBarMetric);
    },
    async handleDistOptionsChange(distTab, dimension, metric, pixelIds = []) {
      this.distLoading = true;
      this.selectedDistMetric = metric;
      this.selectedDistPixelIds = pixelIds;
      if (_.isEmpty(distTab)) {
        if (_.isEmpty(dimension)) {
          this.selectedDistDimension = this.selectedTab;
        } else {
          this.selectedDistDimension = dimension;
        }
      } else {
        this.selectedDistDimension = distTab;
      }

      this.distRows = await appConsoleDataLayer.fetchSearchDistributionData(
        this.advertiserId,
        this.activeSolutionId,
        this.selectedDistDimension,
        this.selectedDistMetric,
        this.selectedDistPixelIds,
        this.payload
      );
      this.distLoading = false;
    },
    async handleBarMetricOptionsChange(barTab, dimension, metric, pixelIds = []) {
      this.barMetricLoading = true;
      this.selectedBarMetric = metric;
      this.selectedBarPixelIds = pixelIds;
      if (_.isEmpty(barTab)) {
        if (_.isEmpty(dimension)) {
          this.selectedBarDimension = this.selectedTab;
        } else {
          this.selectedBarDimension = dimension;
        }
      } else {
        this.selectedBarDimension = barTab;
      }

      this.barMetricRows = await appConsoleDataLayer.fetchSearchMetricDistributionData(
        this.advertiserId,
        this.activeSolutionId,
        this.selectedBarDimension,
        this.selectedBarMetric,
        this.selectedBarPixelIds,
        this.payload
      );
      this.barMetricLoading = false;
    },
    async handlePATabClick(paTab) {
      this.paLoading = true;
      this.selectedPADimension = paTab;
      await this.loadPerformanceData();
    },
    handlePaSortDirectionChange(direction) {
      this.paSortDirection = direction;
    },
    handleSortByChange(value) {
      this.paSortBy = value;
    },
  },
};
</script>
<style lang="scss" scoped>
#performanceAnalysis {
  scroll-margin: 75px;
}
.expan {
  &.expd {
    height: calc(100vh - 90px) !important;
    max-height: calc(100vh - 90px) !important;
  }
}
.bdc_main_wrap.whero {
  /* margin-top: 70px; */
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
}
.body-content.fullmax .thw_content {
  max-width: 100%;
}
.thw_content {
  max-width: 100%;
  margin: auto;
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
  padding: 0px;
  position: relative;
  z-index: 11;
}
.sopened .thw_content {
  max-width: 100%;
}
.internal-sub-nav {
  display: inline-block;
  float: none;
  margin: 11px 20px 31px 5px;
  display: block;
}
.isn-item {
  display: inline-block;
  margin-right: 20px;
  font-size: 14px;
  font-weight: 600;
  color: var(--primarydark2);
  position: relative;
  cursor: pointer;
}

.isn-item.active {
  color: var(--primarydark);
}

.isn-item.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.isn-item.active::before {
  content: '';
  width: 100%;
  height: 2px;
  margin-top: 25px;
  position: absolute;
  background-color: var(--primarycolor);
}

.isn-item:hover {
  color: var(--primarydark);
}

.isn-item:hover::before {
  content: '';
  width: 100%;
  height: 2px;
  margin-top: 25px;
  position: absolute;
  background-color: var(--primarydark2);
}

.bdc_panel {
  padding: 19px 25px 25px 25px;
  border-radius: 10px;
  box-shadow: 0px 10px 30px 0px rgba(0, 40, 60, 0.07);
  box-shadow: 0px 14px 35px -12px rgba(0, 40, 100, 0.17);
  background-color: #fff;
  margin-bottom: 30px;
  border: 1px solid rgb(165 173 178 / 22%);
}
@media (max-width: 900px) {
  .bdc_panel.two-thirds {
    width: 100% !important;
  }
}
.bdc_panel.two-thirds {
  width: calc(65% - 15px);
  display: inline-block;
}
@media (max-width: 900px) {
  .bdc_panel.one-thirds {
    width: 100% !important;
  }
}

.bdc_panel.one-thirds {
  width: calc(35% - 15px);
  display: inline-block;
}

.bdc_panel.last {
  float: right;
}

.bdc_panel .dropdown {
  border: 1px solid #c8d3e4;
}
.height430 {
  height: 430px;
}
.height495 {
  height: 495px;
}
</style>
